import { usePendingSickLeavesCount } from "@/features/leaves/hooks/useLeaves";
import { WhatsNewWidget } from "@/features/whats_new/components/WhatsNewWidget";
import { useAuthStore } from "@/store/auth";
import { getRoleLabel } from "@/utils/roleLabel";
import {
  accessibleDlcModules,
  DLC_BADGE_GLOSS,
  type DlcModuleMeta,
} from "@/utils/dlc";
import { clsx } from "clsx";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";

export function DashboardPage() {
  const user = useAuthStore((s) => s.user);
  const isDirectorOrHR =
    user?.role === "director" ||
    user?.role === "hr" ||
    user?.role === "accountant";
  const { data: pendingSickLeavesCount = 0 } =
    usePendingSickLeavesCount(isDirectorOrHR);
  const roleLabel: Record<string, string> = {
    director: "Dyrektora",
    coordinator: "Koordynatora",
    accountant: "Księgowego",
    employee: "Pracownika",
    contractor: "Podwykonawcy",
  };
  const displayRole = user?.is_ceo
    ? "Zarządu"
    : (roleLabel[user?.role ?? ""] ?? user?.role);
  // DLC modules the current user is granted (CEO → all, contractors → none).
  const dlcModules = accessibleDlcModules(user?.accessible_dlc_modules);

  return (
    <div>
      <h1 className="text-2xl font-mono font-bold text-[var(--text-primary)] mb-1">
        Witaj, {user?.first_name}!
      </h1>
      <p className="text-gray-500 dark:text-gray-400 mb-8">
        Panel {displayRole}
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <DashCard
          title="Wersja"
          value="ELGW v5.3"
          sub={user?.role !== "contractor" ? <em>{"PIDGEOTTO"}</em> : ""}
        />
        <DashCard
          title="Status systemu"
          value="Aktywny"
          sub="Wszystkie serwisy online"
        />
        <DashCard
          title="Rola"
          value={getRoleLabel(user?.role ?? "", user?.is_ceo)}
          sub={
            user?.has_orderer_permission
              ? "Uprawnienie: Zamawiający"
              : "Standardowy dostęp"
          }
        />
        {user?.role !== "contractor" && (
          <DashCard
            title="Oddział"
            value={user?.branch?.name ?? "—"}
            sub="Twój oddział GK ELEKTIM"
            sub2={user?.is_ceo ? "Widok pełny wszystkich oddziałów" : undefined}
          />
        )}
        {isDirectorOrHR && pendingSickLeavesCount > 0 && (
          <Link to="/sick-leaves/review" className="contents">
            <DashCard
              title="L4 — do weryfikacji"
              value={String(pendingSickLeavesCount)}
              sub="Niezweryfikowane zwolnienia lekarskie"
              urgent
            />
          </Link>
        )}
      </div>

      <WhatsNewWidget />

      {dlcModules.length > 0 && (
        <div className="mt-8">
          <p className="text-xs font-mono uppercase tracking-widest text-[var(--text-muted)] mb-3">
            Moduły DLC
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {dlcModules.map((m) => (
              <DlcModuleTile key={m.id} module={m} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function DashCard({
  title,
  value,
  sub,
  sub2,
  urgent = false,
}: {
  title: string;
  value: string;
  sub: React.ReactNode;
  sub2?: string;
  urgent?: boolean;
}) {
  return (
    <div
      className={clsx(
        "group relative p-5 transition-all duration-150",
        "border bg-[var(--color-bg-surface)]",
        // Tactile "lift" on hover using the app's signature hard-offset shadow.
        "hover:-translate-x-px hover:-translate-y-px",
        urgent
          ? "border-amber-300 bg-amber-50 dark:border-amber-700/60 dark:bg-amber-900/20 hover:shadow-[3px_3px_0_#f59e0b]"
          : "border-[var(--color-border)] hover:border-[var(--color-border-strong)] hover:shadow-[3px_3px_0_var(--color-primary)]",
      )}
    >
      {/* Left identity accent — solid bar instead of a flat colour wash. */}
      <span
        aria-hidden
        className={clsx(
          "absolute inset-y-0 left-0 w-1",
          urgent ? "bg-amber-400" : "bg-[var(--color-primary)]",
        )}
      />
      <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-[var(--text-muted)] mb-1.5 pl-1">
        {title}
      </p>
      <p className="text-[1.75rem] leading-none font-mono font-bold text-[var(--text-primary)] pl-1">
        {value}
      </p>
      <p className="text-xs text-[var(--text-muted)] mt-2 pl-1">{sub}</p>
      {sub2 && (
        <p className="text-[10px] text-[var(--text-muted)] mt-0.5 pl-1 opacity-70">
          {sub2}
        </p>
      )}
    </div>
  );
}

/* A DLC module as a clickable tile carrying the module's own gradient identity
   plus the same glossy "glass" sheen used by the sidebar code badges
   (DLC_BADGE_GLOSS: top highlight + animated shine). No red card chrome here —
   the gradient IS the tile. */
function DlcModuleTile({ module }: { module: DlcModuleMeta }) {
  return (
    <Link
      to={module.to}
      className={clsx(
        "group flex min-h-[7.5rem] flex-col justify-between p-5",
        "transition-transform duration-150 hover:-translate-y-0.5",
        DLC_BADGE_GLOSS,
        module.gradient,
      )}
    >
      <div className="relative z-10 flex items-center justify-between">
        <span className="flex items-center gap-1.5">
          <span className="rounded bg-white/20 px-1.5 py-0.5 text-[10px] font-mono font-bold uppercase tracking-wider leading-none ring-1 ring-inset ring-white/40">
            {module.badge}
          </span>
          {module.extension && (
            // Secondary scope folded into this module — a lighter, "+ prefixed"
            // chip so it reads as an extension of the main code, not a peer.
            <span className="rounded bg-white/10 px-1.5 py-0.5 text-[10px] font-mono font-bold uppercase tracking-wider leading-none ring-1 ring-inset ring-white/25">
              {"+ "}
              {module.extension.badge}
            </span>
          )}
        </span>
        <ArrowUpRight
          size={16}
          className="opacity-70 transition-opacity group-hover:opacity-100"
        />
      </div>
      <div className="relative z-10">
        <p className="text-xl font-mono font-bold leading-tight">
          {module.label}
        </p>
        {module.extension ? (
          <p className="mt-1 text-[11px] font-mono opacity-80">
            {"+ "}
            {module.extension.label}
          </p>
        ) : (
          <p className="mt-1 text-[11px] font-mono opacity-80">Moduł DLC</p>
        )}
      </div>
    </Link>
  );
}
